import React, { useCallback, useEffect, useState } from 'react'
import cn from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, Link } from 'react-router-dom'
import notify from '../../utilities/notify'
import { ObjectParam, useQueryParams } from 'use-query-params'
import jobQuery from '../../graphql/queries/job'
import ticketSearchQuery from '../../graphql/queries/ticketsSearch'
import resourceSearchQuery from '../../graphql/queries/resourcesSearch'
import QUERY_KEYS from '../../graphql/queryKeys'
import { history } from '../../history'
import { googleMapsLink } from '../../utilities/locationUtilities'
import NormalLayoutContainer from '../shared/NormalLayoutContainer'
import { PageTitle } from '../../components/page-title'
import JobStatusLabel from '../../components/JobStatusLabel'
import StaticGoogleMap from '../shared/locationPickers/StaticGoogleMap'
import { Spinner } from '../shared/Spinner'
import styles from './index.module.scss'
import JobDisplay from '../hauler/jobs/_id/index/components/JobDisplay'
import JobActionButton from '../hauler/jobs/_id/index/components/JobActionButton'
import JobResourcesDisplay from '../hauler/jobs/_id/index/components/JobResourcesDisplay'
import JobTicketsTable from '../hauler/jobs/_id/index/components/JobTicketsTable'
import JobActivitiesDisplay from '../hauler/jobs/_id/index/components/JobActivitiesDisplay'
import { ROUTES } from '../../routes'
import useQuery from '../../hooks/useQuery'
import useMutation from '../../hooks/useMutation'
import updateJob from '../../graphql/mutations/updateJob'
import addAssetToJobMutation from '../../graphql/mutations/addAssetToJob'
import deleteAssetFromJobMutation from '../../graphql/mutations/deleteAssetFromJob'
import SMSEnableButton from '../../components/sms-enable-button'
import destroyJob from '../../graphql/mutations/destroyJob'
import destroyTickets from '../../graphql/mutations/destroyTickets'
import activitiesPages from '../../graphql/queries/activitiesPages'
import { RECEIVE_CURRENT_JOB_ACTIVITIES_SUCCESS } from '../hauler/jobs/reducers/currentJobActivitiesReducer'
import { handleValidateAndNotify } from '../../utilities/errorHandlers'
import recurringTicketsQuery from '../../graphql/queries/recurringTickets'
import RecurringServicesTable from '../../components/recurring-services/RecurringServicesTable'
const pageSize = 10

export default function JobPage () {
  const { id } = useParams()
  const dispatch = useDispatch()
  const [openTicketsFakeCount, setOpenTicketsFakeCount] = useState(null)
  const [closedTicketsFakeCount, setClosedTicketsFakeCount] = useState(null)
  const [recurringTicketsFakeCount, setRecurringTicketsFakeCount] = useState(null)
  const [assetsFakeCount, setAssetsFakeCount] = useState(null)
  const [openTicketsPager, setOpenTicketsPager] = useState(null)
  const [closedTicketsPager, setClosedTicketsPager] = useState(null)
  const [recurringTicketsPager, setRecurringTicketsPager] = useState(null)
  const [assetsPager, setAssetsPager] = useState(null)
  const [isSMSEnabled, setIsSMSEnabled] = useState(null)
  const {
    dataLoaders,
    form: { jobResourcesSearchForm },
    user,
    hauler,
    currentJobActivities
  } = useSelector((
    {
      dataLoaders,
      user,
      form,
      currentJobActivities
    }
  ) => ({
    dataLoaders,
    user: user.user,
    hauler: user.hauler,
    form,
    currentJobActivities
  }))

  const [queryParams, setQueryParams] = useQueryParams({
    sort: ObjectParam,
    pager: ObjectParam
  })

  const { data: currentJobData, isFetching: currentJobFetching, refetch: refetchJob } = useQuery([
    QUERY_KEYS.job,
    id
  ],
  jobQuery,
  {
    onError () {
      notify('error', 'Error fetching job')
    }
  })
  const fetchCurrentJobActivities = useCallback((page = 1) => {
    activitiesPages({
      dataLoaders,
      queryKey: [
        QUERY_KEYS.activitiesPages,
        {
          entity: 'JOB',
          id,
          page
        }
      ]
    })
      .then((resp) => {
        dispatch({ type: RECEIVE_CURRENT_JOB_ACTIVITIES_SUCCESS, payload: resp.activitiesPages })
      })
      .catch(() => {
        notify('error', 'Error fetching job activities')
      })
  }, [dataLoaders, dispatch, id])

  const { data: openTickets, isFetching: openTicketsFetching, refetch: refetchOpenJobs } = useQuery([
    QUERY_KEYS.ticketsSearch,
    user.haulerId,
    10,
    { column: 'date', direction: 'asc' },
    { jobId: id, status: 'active' },
    openTicketsPager
  ],
  ticketSearchQuery,
  {
    enabled: Boolean(id),
    onError: (error) => handleTicketsError(error),
    placeholderData: { ticketsSearch: { nodes: [], totalCount: 0 } },
    keepPreviousData: true
  }
  )

  const { data: closedTickets, isFetching: closedTicketsFetching, refetch: refetchClosedJobs } = useQuery([
    QUERY_KEYS.ticketsSearch,
    user.haulerId,
    10,
    { column: 'date', direction: 'desc' },
    { jobId: id, status: 'terminal' },
    closedTicketsPager
  ],
  ticketSearchQuery,
  {
    enabled: Boolean(user.haulerId) && Boolean(id),
    onError: (error) => handleTicketsError(error),
    placeholderData: { ticketsSearch: { nodes: [], totalCount: 0 } },
    keepPreviousData: true
  }
  )

  const { data: recurringTickets, isFetching: recurringTicketsFetching } = useQuery([
    QUERY_KEYS.recurringTickets,
    id,
    10,
    recurringTicketsPager
  ],
  recurringTicketsQuery,
  {
    enabled: Boolean(id) && hauler.isRecurringServiceEnabled,
    onError: (error) => handleError(error, 'Error fetching recurring services'),
    placeholderData: { recurringTickets: { nodes: [], totalCount: 0 } },
    keepPreviousData: true
  }
  )

  const { data: assets, isFetching: assetsFetching, refetch: refetchAssets } = useQuery([
    QUERY_KEYS.assetsOnJob,
    {
      haulerId: user.haulerId,
      sort: queryParams.sort,
      jobId: id,
      assetsPager
    }
  ],
  resourceSearchQuery,
  {
    enabled: Boolean(user.haulerId),
    onError: (error) => handleError(error, 'Error fetching resources'),
    refetchOnWindowFocus: false
  })

  const { data: allAssets, isFetching: allAssetsFetching, refetch: refetchAllAssets } = useQuery([
    QUERY_KEYS.allAssets,
    {
      haulerId: user.haulerId,
      sort: {
        column: 'uid',
        direction: 'asc'
      },
      notJobId: id
    }
  ],
  resourceSearchQuery,
  {
    enabled: Boolean(user.haulerId),
    onError: (error) => handleError(error, 'Error fetching resources'),
    refetchOnWindowFocus: false
  })

  const { mutateAsync: updateSMS, isLoading: isUpdatingSMS } = useMutation(updateJob, {
    onSuccess (data) {
      refetchJob()
      notify('success', `Successfully ${data.job.smsActive ? 'Enabled' : 'Disabled'} SMS.`)
    },
    onError () {
      notify('error', 'Failed to enable/disable SMS on job')
    }
  })

  const { mutateAsync: updateStatus, isLoading: isUpdatingStatus } = useMutation(updateJob, {
    onSuccess (data) {
      refetchJob()
      notify('success', `Successfully ${data.job.status === 'open' ? 'opened' : 'closed'} job.`)
    },
    onError () {
      notify('error', 'Failed to open/close job')
    }
  })

  const { mutate: addAssetToJob, isLoading: addAssetToJobLoading } = useMutation(addAssetToJobMutation, {
    onSuccess: (data) => {
      refetchAssets()
      notify('success', 'Asset Updated Successfully!')
    },
    onError: error => {
      console.error(error)
      notify('error', 'Failed to Update Asset')
    },
    onSettled: refetchAllAssets
  })

  const { mutate: deleteAssetFromJob, isLoading: deleteAssetFromJobLoading } = useMutation(deleteAssetFromJobMutation, {
    onSuccess: (data) => {
      refetchAssets()
      notify('success', 'Asset Successfully Removed from Job!')
    },
    onError: error => {
      console.error(error)
      notify('error', 'Failed to Remove Asset')
    },
    onSettled: refetchAllAssets
  })

  const { mutate: deleteJob } = useMutation(destroyJob, {
    onSuccess ({ resourceImport }) {
      history.replace('/hauler/jobs/search')
      notify('success', 'Job Deleted')
    },
    onError (error) {
      handleValidateAndNotify(error, 'Failed to Destroy Job')
    }
  })

  const { mutate: deleteBatchTickets } = useMutation(destroyTickets, {
    onSuccess ({ resourceImport }) {
      refetchOpenJobs()
      refetchClosedJobs()
      notify('success', 'Tickets Deleted')
    },
    onError (error) {
      handleValidateAndNotify(error, 'Failed to Destroy Tickets')
    }
  })

  useEffect(function initialSetOfSMSEnabled () {
    if (currentJobData?.job && isSMSEnabled === null) {
      setIsSMSEnabled(currentJobData.job.smsActive)
    }
  }, [currentJobData, isSMSEnabled])

  useEffect(function toggleFakeOpenTicketsPagination () {
    if (!openTickets.ticketsSearch.totalCount || openTicketsFetching || openTicketsFakeCount !== null) return
    setOpenTicketsFakeCount({ start: 1, end: Math.min(pageSize, openTickets.ticketsSearch.totalCount) })
  }, [openTickets.ticketsSearch.totalCount, openTickets, openTicketsFakeCount, setOpenTicketsFakeCount, openTicketsFetching])

  useEffect(function toggleFakeClosedTicketsPagination () {
    if (!closedTickets.ticketsSearch.totalCount || closedTicketsFetching || closedTicketsFakeCount !== null) return
    setClosedTicketsFakeCount({ start: 1, end: Math.min(pageSize, closedTickets.ticketsSearch.totalCount) })
  }, [closedTickets, closedTicketsFakeCount, setClosedTicketsFakeCount, closedTicketsFetching])

  useEffect(function toggleFakeRecurringTicketsPagination () {
    if (!recurringTickets.recurringTickets.totalCount || recurringTicketsFetching || recurringTicketsFakeCount !== null) return
    setRecurringTicketsFakeCount({ start: 1, end: Math.min(pageSize, recurringTickets.recurringTickets.totalCount) })
  }, [recurringTickets, recurringTicketsFakeCount, setRecurringTicketsFakeCount, recurringTicketsFetching])

  function handleTicketsError () {
    notify('error', 'Failed to retrieve job tickets')
  }

  const handleEditJob = () => {
    history.push(`/hauler/jobs/${id}/edit`)
  }

  const onJobActivitiesPageRequest = (page = 1) => {
    fetchCurrentJobActivities(page)
  }

  const handleNewTicket = () => {
    const ticketParams = new URLSearchParams()
    if (id) ticketParams.append('job_id', id)
    history.push(`/hauler/tickets/new?${ticketParams.toString()}`)
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    addAssetToJob({ assetId: jobResourcesSearchForm.values.id, jobId: id })
  }

  const handleRemoveResourceFromJob = (assetId) => {
    deleteAssetFromJob({ assetId: Number(assetId), setOnSelf: false })
  }

  function handleError (error, message) {
    console.error(error)
    notify('error', message)
  }

  const destroyCurrentJob = (job) => {
    deleteJob({ jobId: job.id })
  }

  const onBatchDelete = (ids) => {
    // eslint-disable-next-line
    const confirmed = confirm(`Are you sure you want to delete ${ids.length} tickets? This CANNOT BE UNDONE.`);
    if (confirmed) {
      deleteBatchTickets({ ids: ids?.map((e) => ({ id: e })) })
    }
  }

  const onBatchEdit = (ids) => {
    history.push(`${ROUTES.bulkEditTicketsPage}?ids=${ids.join(',')}`)
  }

  function onOpenTicketsPageRequest (direction, cursor) {
    setOpenTicketsPager({ cursor, direction, pageSize })

    if (!openTicketsFakeCount) return
    if (direction === 'before') {
      setOpenTicketsFakeCount(prevFakeCount => ({ start: prevFakeCount.start - pageSize, end: prevFakeCount.end - pageSize }))
    }
    if (direction === 'after') {
      setOpenTicketsFakeCount(prevFakeCount => ({ start: prevFakeCount.start + pageSize, end: prevFakeCount.end + pageSize }))
    }
  }

  function onClosedTicketsPageRequest (direction, cursor) {
    setClosedTicketsPager({ cursor, direction, pageSize })

    if (!closedTicketsFakeCount) return
    if (direction === 'before') {
      setClosedTicketsFakeCount(prevFakeCount => ({ start: prevFakeCount.start - pageSize, end: prevFakeCount.end - pageSize }))
    }
    if (direction === 'after') {
      setClosedTicketsFakeCount(prevFakeCount => ({ start: prevFakeCount.start + pageSize, end: prevFakeCount.end + pageSize }))
    }
  }

  function onRecurringTicketsPageRequest (direction, cursor) {
    setRecurringTicketsPager({ cursor, direction, pageSize })

    if (!recurringTicketsFakeCount) return
    if (direction === 'before') {
      setRecurringTicketsFakeCount(prevFakeCount => ({ start: prevFakeCount.start - pageSize, end: prevFakeCount.end - pageSize }))
    }
    if (direction === 'after') {
      setRecurringTicketsFakeCount(prevFakeCount => ({ start: prevFakeCount.start + pageSize, end: prevFakeCount.end + pageSize }))
    }
  }

  function onAssetsPageRequest (direction, cursor) {
    setAssetsPager({ cursor, direction, pageSize })

    if (!closedTicketsFakeCount) return
    if (direction === 'before') {
      setAssetsFakeCount(prevFakeCount => ({ start: prevFakeCount.start - pageSize, end: prevFakeCount.end - pageSize }))
    }
    if (direction === 'after') {
      setAssetsFakeCount(prevFakeCount => ({ start: prevFakeCount.start + pageSize, end: prevFakeCount.end + pageSize }))
    }
  }

  useEffect(() => {
    fetchCurrentJobActivities(1)
  }, [id, fetchCurrentJobActivities])

  useEffect(function setDefaultQueryParams () {
    const defaultQueryParams = {}
    if (!queryParams.sort) {
      defaultQueryParams.sort = { column: 'lastMoved', direction: 'asc' }
    }
    setQueryParams(defaultQueryParams, 'replaceIn')
  }, [id, assets, queryParams.sort, setQueryParams])

  async function handleSMSUpdate (isEnabledAtTimeOfClick) {
    await updateSMS({
      latitude: parseFloat(currentJobData.job.latitude),
      longitude: parseFloat(currentJobData.job.longitude),
      smsActive: !isEnabledAtTimeOfClick,
      id
    })
    setIsSMSEnabled(!isEnabledAtTimeOfClick)
  }

  async function handleStatusUpdate (newStatus) {
    await updateStatus({
      latitude: parseFloat(currentJobData.job.latitude),
      longitude: parseFloat(currentJobData.job.longitude),
      status: newStatus,
      onlyUpdateStatus: true,
      id
    })
  }

  return (
    <div>
      <NormalLayoutContainer
        showBackLink>
        <div className='container-fluid'>
          <PageTitle title={`Job #${currentJobData?.job?.customId || ''} for ${currentJobData?.job?.client?.name || ''}`}>

            <div>
              <h4 className='push-down-very-small pull-right'>
                Current Status:
                <span className='space-left'>
                  <JobStatusLabel status={currentJobData?.job?.status} />
                </span>
              </h4>
              <div>
                <div className='pull-right'>
                  <div className={cn(styles.headerButtonWrapper)}>
                    <button
                      className={cn('btn btn-lg btn-primary', styles.button)}
                      onClick={handleNewTicket}>
                      <span className='hidden-xs pull-left'>Create New Ticket</span>
                      <i className='material-icons pull-right thumbster-button-icon no-margin-bs-xs'>add</i>
                    </button>
                    <Link
                      className={cn('btn btn-lg btn-primary', styles.button)}
                      to={`${ROUTES.bulkCreateTickets}?job_id=${currentJobData?.job?.id}`}>
                      <span className='hidden-xs pull-left'>Bulk Create Tickets</span>
                      <i className='material-icons pull-right thumbster-button-icon no-margin-bs-xs'>add_circle</i>
                    </Link>
                    <JobActionButton
                      onSubmit={handleStatusUpdate}
                      disabled={currentJobFetching || isUpdatingStatus}
                      status={currentJobData?.job?.status}
                    />
                    <div>
                      <SMSEnableButton
                        smsEnabled={isSMSEnabled}
                        handleUpdate={handleSMSUpdate}
                        isLoading={currentJobFetching || isUpdatingSMS}
                        step={'JOB'}
                        isParentEnabled={currentJobData?.job.client.smsActive}
                        levelName={currentJobData?.job?.customId}
                      />
                    </div>
                  </div>
                </div>
                <div className='pull-right' />
              </div>

            </div>

          </PageTitle>

          <br />
          {
            currentJobFetching
              ? <Spinner isFetching />
              : <div>
                <div className='row'>
                  <div className='col-xs-12 col-md-6'>
                    <JobDisplay
                      updateSMS={updateSMS}
                      isSMSLoading={isUpdatingSMS}
                      job={currentJobData?.job}
                      handleEdit={handleEditJob}
                    />
                  </div>
                  <div className='col-xs-12 col-md-5 col-md-offset-1'>
                    {
                      assetsFetching || addAssetToJobLoading || deleteAssetFromJobLoading || allAssetsFetching
                        ? <Spinner isFetching />
                        : <div>
                          <JobResourcesDisplay
                            jobCustomId={currentJobData?.job?.customId}
                            jobResources={assets?.assets}
                            allResources={allAssets?.assets?.nodes}
                            handleSubmit={handleSubmit}
                            handleRemoveResourceFromJob={handleRemoveResourceFromJob}
                            onPageRequest={onAssetsPageRequest}
                            fakeCount={assetsFakeCount}
                          />
                        </div>
                    }
                  </div>
                  <div className='col-xs-12'>
                    {
                      currentJobFetching
                        ? <Spinner isFetching />
                        : <div className='list-group row'>
                          <div className='list-group-item thumbster-gray-super-light-bg'>
                            <h5>
                              Job Location
                              &nbsp;
                              <a
                                href={() => googleMapsLink(currentJobData?.job)}
                                target='_blank'
                                rel='noopener noreferrer'>
                                <small className='text-primary link'>(View in Google Maps)</small>
                              </a>
                            </h5>
                          </div>
                          <div className='list-group-item'>
                            <StaticGoogleMap
                              mapClass='static-google-map-jobs'
                              latitude={currentJobData?.job?.latitude}
                              longitude={currentJobData?.job?.longitude}
                              place_id={currentJobData?.job?.placeId}
                              showLatLngInfo={true}
                              addrComponents={currentJobData?.job || ''}
                            />
                          </div>
                        </div>
                    }
                  </div>
                </div>
                {hauler.isRecurringServiceEnabled &&
                  <div className='row' id='recurring-services-scroll-container'>
                    <div className={cn(styles.recurringServicesTable, 'col-xs-12')}>
                      <RecurringServicesTable
                        recurringServices={recurringTickets?.recurringTickets}
                        onPageRequest={onRecurringTicketsPageRequest}
                        fakeCount={recurringTicketsFakeCount}
                        job={currentJobData?.job}
                      />
                    </div>
                  </div>
                }
                <div className='row' id='open-tickets-scroll-container'>
                  <div className='col-xs-12'>
                    <JobTicketsTable
                      title='Open Tickets'
                      handleCreateTicket={handleNewTicket}
                      job={currentJobData?.job}
                      tickets={openTickets?.ticketsSearch}
                      onPageRequest={onOpenTicketsPageRequest}
                      fakeCount={openTicketsFakeCount}
                      onBatchDelete={onBatchDelete}
                      onBatchEdit={onBatchEdit}
                    />
                  </div>
                </div>
                <div className='row' id='terminal-tickets-scroll-container'>
                  <div className='col-xs-12'>
                    <JobTicketsTable
                      title='Closed Tickets'
                      job={currentJobData?.job}
                      tickets={closedTickets?.ticketsSearch}
                      onPageRequest={onClosedTicketsPageRequest}
                      fakeCount={closedTicketsFakeCount}
                      onBatchDelete={onBatchDelete}
                      onBatchEdit={onBatchEdit}
                    />
                  </div>
                </div>
                {
                  currentJobActivities.isFetching
                    ? <Spinner isFetching />
                    : <JobActivitiesDisplay
                        activities={currentJobActivities.activities}
                        pagination={currentJobActivities.pagination}
                        onPageRequest={onJobActivitiesPageRequest}
                      />
                }
                <button
                  className='btn btn-link center-block'
                  onClick={() => {
                    const result = window.confirm(
                      'THIS CANNOT BE UNDONE! Deleting a job will remove any record ' +
                      'that it existed including its tickets.')
                    if (result) {
                      destroyCurrentJob(currentJobData?.job)
                    }
                  }}>
                  <span className='pull-left'>Destroy Job</span>
                  <i className='material-icons pull-right thumbster-button-icon'>delete</i>
                </button>
                <br />
              </div>
          }
        </div>

      </NormalLayoutContainer>
    </div>
  )
}
